import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { FormProvider, useForm } from 'react-hook-form'
import {
    Badge
    , Box
    , Button
    , Card
    , CardActions, CardContent
    , CardHeader
    , Chip
    , Collapse
    , Drawer
    , IconButton
    , List
    , ListItem
    , ListItemText
    , Skeleton
    , styled
    , Typography
    , useTheme
} from '@mui/material/'
import useHttp from '../hooks/useHttp'
import useLogout from '../hooks/useLogout'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat' // ES 2015
import FilterFormElement from '../components/forms/FilterFormElement'
import OrganizationIcon from '../components/OrganizationIcon'
import { useActionLogs } from '../store/actionLogsContext'
import { ThemeContext } from '../App'
import { NewApiBaseUrl } from "../consts/new-api-base-url";
import { DESKTOP_HISTORY_DRAWER_WIDTH_PX } from "../consts/form-drawer-width";
import useHttpApiNew from "../hooks/useHttpApiNew";
import { Divider } from "@mui/material";
import SvgCustomIcon from "../components/icons/SvgCustomIcon";
import { canUserAccessActionHistory } from './Statistics/statistics-report-type';

dayjs.extend(customParseFormat)

// eslint-disable-next-line no-restricted-globals
// const logsBaseUrl = new URL('/api/action_log', location)
// eslint-disable-next-line no-restricted-globals
// const getUsersUrl = new URL('/api/users', location)
const getUsersListUrl = new URL(`${NewApiBaseUrl}/Auth/GetList`)

const StyledDrawer = styled(Drawer)(() => ({
    width: DESKTOP_HISTORY_DRAWER_WIDTH_PX
    , '& .MuiDrawer-paper': {
        width: DESKTOP_HISTORY_DRAWER_WIDTH_PX
        , height: '100vh'
    }
}))

const StyledBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': { top: 10 }
}))

const ExpandMore = styled(props => {
    const { expand, ...other } = props
    return <IconButton {...other} />
})(({ theme }) => ({
    marginLeft: 'auto'
    , transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest
    })
    , variants: [
        {
            props: ({ expand }) => !expand
            , style: {
                transform: 'rotate(0deg)'
            }
        }
        , {
            props: ({ expand }) => !!expand
            , style: {
                transform: 'rotate(180deg)'
            }
        }
    ]
}))

const TableSkel = ({ length = 5 }) => ( // ? <CircularProgress sx={{ mt: 2 }} />
    <>
        <Skeleton width='100%' sx={{ mt: 2, fontSize: '2rem' }} />
        {Array.from({ length }).map((_, i) => <Skeleton key={i} width='100%' />)}
        {/* <Skeleton width='100%' sx={{ fontSize: '2rem' }} /> */}
    </>
)

export default function History({ position, open, isMobile, getCrash, onClose }) {
    const navigate = useNavigate()
    const location = useLocation()
    const logout = useLogout()
    const { logs, setAsRead, fetchLogs } = useActionLogs()
    const state = useMemo(() => location.state || {}, [location.state])
    const token = (() => {
        if (state?.token) return state.token
        try {
            const raw = localStorage.getItem('adams_user')
            const user = raw ? JSON.parse(raw) : null
            return user?.token || localStorage.getItem('token') || null
        } catch {
            return localStorage.getItem('token') || null
        }
    })()
    const theme = useTheme()
    const { _ } = useLingui()
    const { isLoading, error, sendRequest } = useHttp()
    const [expanded, setExpanded] = useState(false)
    // Note: formDefaultValues is defined after historyFilterElements, so we'll set defaultValues in useEffect
    const methods = useForm({
        defaultValues: {}
    })
    const { request } = useHttpApiNew()
    const submitRef = useRef()
    const [users, setUsers] = useState([])
    const { mode, toggleTheme } = useContext(ThemeContext)

    const sessionUser = useMemo(() => {
        if (state?.init?.user) return state.init.user
        try {
            const raw = localStorage.getItem('adams_user')
            const stored = raw ? JSON.parse(raw) : null
            return stored?.init?.user ?? null
        } catch {
            return null
        }
    }, [state?.init?.user])

    useEffect(() => {
        if (!canUserAccessActionHistory(sessionUser)) {
            navigate('/home', { state: { ...state, isHistoryOpen: false }, replace: true })
        }
    }, [sessionUser, navigate, state])

    const historyFilterElements = useMemo(() => [
        {
            label: _(msg`Date`)
            , isRequired: false
            , margin: 'dense'
            , type: 'date'
            , value: ''
            , stack: true
        }
        , {
            label: _(msg`User`)
            , isRequired: false
            , margin: 'dense'
            , type: 'select'
            , value: 0
            , options: [
                { label: _(msg`None`), value: 0 }
                , ...users?.map(user => ({ label: user.username, value: user.id })) || []
            ]
        }
        , {
            label: _(msg`Action`)
            , isRequired: false
            , margin: 'dense'
            , type: 'select'
            , value: 0
            , options: [
                { label: _(msg`None`), value: 0 }
                , { label: _(msg`Added crash`), value: 'Added crash' }
                , { label: _(msg`Validated crash`), value: 'Validated crash' }
                , { label: _(msg`Registered new user`), value: 'Registered new user' }
                , { label: _(msg`Modified user`), value: 'Modified user' }
            ]
        }
        , {
            label: _(msg`Data`)
            , isRequired: false
            , margin: 'dense'
            , type: 'select'
            , value: 0
            , options: [
                { label: _(msg`None`), value: 0 }
                , { label: _(msg`Police`), value: 1 }
                , { label: _(msg`Healthcare`), value: 2 }
                , { label: _(msg`Insurance`), value: 3 }
            ]
        }
    ], [users, _])

    // Create initial values object for form defaultValues and reset
    // Note: FilterFormElement uses name format: `${formList ? formList[form.number] : ""}.${elem.label}`
    // Since formList is not passed in History.js, the format becomes `.${elem.label}` or `.${elem.label}-${term}`
    const formDefaultValues = useMemo(() => {
        const values = {}
        historyFilterElements.forEach(elem => {
            if (elem.stack) {
                // For date fields with stack, we need to handle Date-From and Date-To
                // DatePicker expects null or a Dayjs object, not an empty string
                // Field name format: `.${elem.label}-From` and `.${elem.label}-To`
                values[`.${elem.label}-From`] = elem.value === '' ? null : elem.value
                values[`.${elem.label}-To`] = elem.value === '' ? null : elem.value
            } else {
                // Field name format: `.${elem.label}`
                values[`.${elem.label}`] = elem.value
            }
        })
        return values
    }, [historyFilterElements])

    const handleExpandClick = () => {
        setExpanded(!expanded)
    }

    const getUsers = useCallback(async () => {
        /*
        sendRequest({
          token
          , url: getUsersUrl
          , manageResData: res => {
            setUsers(res.users)
          }
        })
         */
        const response = await request(getUsersListUrl, 'GET', null, {
            Authorization: `Bearer ${token}`
        })
        if (response.responseAnyError) return;
        setUsers(response.responseData)
    }, [token, request])

    useEffect(() => {
        if (!token) return logout()
        if (isMobile) return navigate('/home', { state })
        getUsers()
    }, [token, logout, isMobile, navigate, state, getUsers])


    const handleFilterObjSubmit = params => {
        let fetchParams = []

        Object.entries(params)
            .filter(([_, value]) => value ?? value)
            .filter(([_, value]) => value !== 'None')
            .forEach(([key, value]) => {
                switch (key) {
                    case 'User':
                        fetchParams = [...fetchParams, ['user_id', value]]
                        break
                    case 'Date-From':
                        fetchParams = [...fetchParams, ['after', dayjs(value).toJSON()]]
                        break
                    case 'Date-To':
                        fetchParams = [...fetchParams, ['before', dayjs(value).toJSON()]]
                        if (dayjs(fetchParams.find(fp => fp[0] === 'after')?.[1]).isValid &&
                            dayjs(fetchParams.find(fp => fp[0] === 'after')?.[1]).isSame(dayjs(), 'day')) {
                            fetchParams = fetchParams.filter(fp => fp[0] !== 'before')
                        }
                        break
                    case 'Action':
                        fetchParams = [...fetchParams, ['message', value]]
                        break
                    case 'Data':
                        fetchParams = [...fetchParams, ['organization', value]]
                        break
                    default:
                }
            })

        fetchLogs(fetchParams)
    }

  

    const getLogIcon = (log) => {
        
        const userId = log.organization === -1 ? log.organization : log.userFk?.basic_organization;
        switch (userId) {
            case 1:
                return 'police'
            case 2:
                return 'healthCrossFill'
            case 3:
                return 'insurance'
        }
        return 'shieldPerson'
    }

    return (
        <StyledDrawer
            variant="persistent"
            anchor={theme.direction === 'rtl' ? 'left' : 'right'}
            open={open}
        >
            <Box sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography
                        sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontWeight: 'bold', fontSize: 20, flex: 1, textAlign: 'center' }}>
                        <Trans>History</Trans>
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ 
                            color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white' }} />
            </Box>

            {isLoading
                ? <TableSkel length={9} />
                : <>
                    <Box>
                        <Card sx={{ m: 1 }}>
                            <CardHeader
                                onClick={handleExpandClick}
                                sx={{ cursor: 'pointer' }}
                                action={
                                    <ExpandMore
                                        sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white' }}
                                        expand={expanded}
                                        aria-expanded={expanded}
                                        aria-label={_(msg`show more`)}
                                    >
                                        <ExpandMoreIcon />
                                    </ExpandMore>
                                }
                                titleTypographyProps={{ fontSize: 14, fontWeight: 'bold', color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white' }}
                                title={_(msg`Filter`)}
                            />
                            <Collapse sx={{ boxShadow: 'none' }} in={expanded} timeout='auto' unmountOnExit>
                                <FormProvider {...methods}>
                                    <CardContent>
                                        <Box
                                            ref={submitRef}
                                            component='form'
                                            onSubmit={methods.handleSubmit(handleFilterObjSubmit)}
                                            display='flex'
                                            flexDirection='column'
                                            alignItems={'center'}
                                            pl={1}
                                            pr={1}
                                            noValidate
                                        >
                                            {historyFilterElements
                                                .map(elem => <FilterFormElement
                                                    key={elem.label}
                                                    elem={elem}
                                                    isMobile={isMobile}
                                                />)}
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <Button
                                            variant='contained'
                                            size='small'
                                            sx={{ background: theme.palette.newColors.cyanGradient, color: theme.palette.newColors.lightGray }}
                                            onClick={() => submitRef.current && submitRef.current.requestSubmit()}
                                        >
                                            <Trans>Filter</Trans>
                                        </Button>
                                        <Button
                                            variant='contained'
                                            size='small'
                                            sx={{ background: theme.palette.newColors.cyanGradient, color: theme.palette.newColors.lightGray }}
                                            onClick={() => {
                                                // Reset form to default values
                                                // Use reset with formDefaultValues to properly reset all fields
                                                const resetValues = { ...formDefaultValues }
                                            
                                                // Reset the form
                                                methods.reset(resetValues, {
                                                    keepDefaultValues: false,
                                                    keepValues: false,
                                                    keepDirty: false,
                                                    keepErrors: false,
                                                    keepIsSubmitted: false,
                                                    keepTouched: false,
                                                    keepIsValid: false,
                                                    keepSubmitCount: false
                                                })
                                                
                                                // Force update each field to ensure they are reset
                                                setTimeout(() => {
                                                    Object.keys(resetValues).forEach(key => {
                                                        methods.setValue(key, resetValues[key], {
                                                            shouldValidate: false,
                                                            shouldDirty: false,
                                                            shouldTouch: false
                                                        })
                                                    })
                                                }, 0)
                                                fetchLogs()
                                            }}
                                        >
                                            <Trans>Reset</Trans>
                                        </Button>
                                    </CardActions>
                                </FormProvider>
                            </Collapse>
                        </Card>
                    </Box>
                    <Box overflow='auto'>
                        <Typography sx={{textAlign:'center', fontSize: 14, fontWeight: 'bold', color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'}}>{_(msg`Found items:`)} {logs?.length}</Typography>
                        {logs && logs?.length > 0 && (
                            <List>
                                {logs.map((log, index) => {
                                    const username = !log.username && !log.userFk ? 'admin' : log?.userFk?.username || log?.username
                                    const iconName = getLogIcon(log)
                                    return (
                                        <ListItem
                                            onClick={async () => {
                                                if (!log.status) await setAsRead(log.id)
                                                if (log.crash_id && !log.message.toLowerCase().includes('deleted')) {
                                                    getCrash(log.crash_id, log.organization, log.user_id)
                                                    onClose?.()
                                                }
                                                if (log.message.toLowerCase().includes('user') ) {
                                                    navigate('/admin', { state: { ...state, isHistoryOpen: false } })
                                                }
                                            }}
                                            key={log.id}
                                            sx={{
                                                flexDirection: 'column'
                                                ,
                                                alignItems: 'stretch'
                                                ,
                                                backgroundColor: index % 2 === 0 ? theme.palette.grey[200] : theme.palette.background.default
                                                ,
                                                cursor: log.crash_id || log.message.includes('user') ? 'pointer' : 'default'
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                                                    <SvgCustomIcon name={iconName} />
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="body2" sx={{ fontSize: 14,fontWeight:'bold' }}>
                                                            {username} 
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 14,fontStyle:'oblique' }}>
                                                            {log.ts ? dayjs(log.ts).format('DD/MM/YYYY HH:mm') : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {!log.status && (
                                                    <Badge color='info' badgeContent=' ' />
                                                )}
                                            </Box>
                                            <ListItemText primaryTypographyProps={{ fontSize: 14 }}
                                                primary={log.message} />
                                        </ListItem>
                                    )
                                })}
                            </List>)
                       
                        }
                    </Box>
                </>}
        </StyledDrawer>
    )
}
